import { useCallback, useContext, useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import { useNavigate } from "react-router-dom";

import {
  ButtonContainer,
  Column,
  ColumnContent,
  ColumnFooter,
  ColumnHeader,
  Counter,
  TaskList,
} from "../Styles";
import {
  InspectionfieldStateEnum,
  QueriesInspectionListQuery as InspectionListQueryType,
} from "../__generated__/QueriesInspectionListQuery.graphql";
import { AuthContext, fetchUserID } from "../../Auth";
import { ColumnDataContext } from "../InspectionList";
import { InspectionListQuery } from "../Queries";
import { openNavigation } from "../../../utils";
import { StateColors, StateTitles, GetTaskSort } from "../StateHelpers";
import { StrictModeDroppable } from "../../../components/StrictModeDroppable";
import { useFilteredData } from "../../../hooks/useFilters";
import { useLazyLoadQuery } from "react-relay";
import Button from "../../../components/Buttons/Button";
import InspectionField, {
  FieldCardButtons,
} from "../../../components/InspectionField";

export interface InspectionListColumnProps {
  state: InspectionfieldStateEnum;
  updateField: Function;
}

export default function InspectionListColumn(props: InspectionListColumnProps) {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const {
    disabledButtons,
    setDisabledButtons,
    planMultiple,
    setPlanMultiple,
    setPlannedDate,
    setShowPlanModal,
    selectedIds,
    setSelectedIds,
    isMutationInFlight,
    isDragging,
  } = useContext(ColumnDataContext);
  const [scrolledToTop, setScrolledToTop] = useState(true);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const data = useLazyLoadQuery<InspectionListQueryType>(InspectionListQuery, {
    userId: authContext?.user ? fetchUserID(authContext?.user) : null,
  });
  const filteredData = useFilteredData(data.allInspectionfields);

  const filterOnState = useCallback(
    (state: InspectionfieldStateEnum) =>
      filteredData?.filter((edge) => {
        return edge?.node?.state === state;
      }),
    [filteredData]
  );
  const handleClickNavigate = (geometry?: any) => {
    openNavigation(geometry);
  };
  const handleNavigate = useCallback(
    (id: string) => {
      navigate(`/b/${id}`);
    },
    [navigate]
  );
  const handleRetry = (node: any) => props.updateField(node, "ASSIGNED");
  const handlePressCalendarIcon = (id: string) => {
    setSelectedIds([id]);
    setShowPlanModal(true);
  };
  const handleSelect = useCallback(
    (id: string) =>
      setSelectedIds((currSelection: string[]) =>
        currSelection?.includes(id)
          ? currSelection.filter((cid) => cid !== id)
          : [...(currSelection || []), id]
      ),
    [setSelectedIds]
  );
  const handlePlanMultipleMode = useCallback(
    (enabled: boolean) => {
      setPlanMultiple(enabled);
      setDisabledButtons(
        enabled
          ? [
              FieldCardButtons.OnClick,
              FieldCardButtons.OnClickFocus,
              FieldCardButtons.OnClickNavigate,
              FieldCardButtons.OnClickPlan,
              FieldCardButtons.OnClickRetry,
            ]
          : undefined
      );
    },
    [setDisabledButtons, setPlanMultiple]
  );
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const targetDiv = event.target as HTMLDivElement;
    const reachedTop = targetDiv.scrollTop < 5;
    const reachedBottom =
      targetDiv.scrollHeight - targetDiv.scrollTop - targetDiv.clientHeight < 5;

    setScrolledToTop(reachedTop);
    setScrolledToBottom(reachedBottom);
  };

  return (
    <Column key={props.state} color={StateColors[props.state]}>
      <ColumnHeader>
        {StateTitles[props.state]}
        <Counter>{filterOnState(props.state)?.length || 0}</Counter>
      </ColumnHeader>
      <ColumnContent
        hideTopFade={scrolledToTop}
        hideBottomFade={scrolledToBottom}
        hideScrollFade={isDragging}
      >
        <StrictModeDroppable droppableId={props.state}>
          {(provided) => (
            <TaskList
              onScroll={handleScroll}
              {...provided.droppableProps}
              ref={provided.innerRef}
              itemType={props.state}
            >
              {filterOnState(props.state)
                ?.sort(GetTaskSort(props.state))
                ?.map((edge, ix: number) => (
                  <Draggable
                    key={edge!.node!.id!}
                    draggableId={edge!.node!.id!}
                    index={ix}
                    isDragDisabled={planMultiple}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <InspectionField
                          field={edge?.node!}
                          selected={selectedIds?.includes(edge!.node!.id)}
                          disabledButtons={disabledButtons}
                          onClick={handleNavigate}
                          onClickSelect={
                            planMultiple ? handleSelect : undefined
                          }
                          onClickRetry={() => handleRetry(edge?.node)}
                          onClickPlan={() => {
                            if (edge?.node?.state === "PLANNED") {
                              setPlannedDate(edge?.node?.plannedDate);
                            }
                            handlePressCalendarIcon(edge?.node?.id!);
                          }}
                          onClickNavigate={
                            edge?.node?.state === "PLANNED" ||
                            edge?.node?.state === "IN_PROGRESS"
                              ? () =>
                                  handleClickNavigate(
                                    edge?.node?.field?.geometry
                                  )
                              : undefined
                          }
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
              {provided.placeholder}
            </TaskList>
          )}
        </StrictModeDroppable>
      </ColumnContent>
      <ColumnFooter>
        {props.state === "ASSIGNED" ? (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "8px",
            }}
          >
            {planMultiple ? (
              <>
                <ButtonContainer>
                  <Button
                    size="full"
                    label="Annuleren"
                    color="dark"
                    transparentBackground
                    onClick={() => {
                      setSelectedIds([]);
                      handlePlanMultipleMode(false);
                    }}
                  />
                </ButtonContainer>
                <ButtonContainer>
                  <Button
                    size="full"
                    label={`Inplannen (${selectedIds?.length || 0})`}
                    color="green"
                    disabled={
                      isMutationInFlight ||
                      (selectedIds && selectedIds.length < 1)
                    }
                    onClick={() => setShowPlanModal(true)}
                  />
                </ButtonContainer>
              </>
            ) : (
              <Button
                size="full"
                label="Meerdere BE's inplannen"
                color="green"
                onClick={() => handlePlanMultipleMode(true)}
              />
            )}
          </div>
        ) : null}
      </ColumnFooter>
    </Column>
  );
}
